/**
 * Utility functions for formatting data
 * @author Muskan Khan
 * @date MAR, 2025
 */

/**
 * Format currency amount
 */
export const formatCurrency = (amount, currencySymbol = '₹') => {
    if (amount === null || amount === undefined) return `${currencySymbol}0.00`;
    
    const num = parseFloat(amount);
    if (isNaN(num)) return `${currencySymbol}0.00`;
    
    return `${currencySymbol}${Math.abs(num).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

/**
 * Format date
 */
export const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    } catch (error) {
        return 'N/A';
    }
};

/**
 * Format date with time
 */
export const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        return 'N/A';
    }
};

/**
 * Format percentage
 */
export const formatPercentage = (value) => {
    if (value === null || value === undefined) return '0%';
    
    const num = parseFloat(value);
    if (isNaN(num)) return '0%';
    
    return `${num.toFixed(1)}%`;
};

/**
 * Format number with commas
 */
export const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return '0';
    
    return new Intl.NumberFormat('en-IN').format(parsed);
};

/**
 * Format file size
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format duration in seconds to human readable format
 */
export const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0) parts.push(`${secs}s`);
    
    return parts.join(' ') || '0s';
};

/**
 * Format relative time (e.g., "2 days ago")
 */
export const formatRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        
        if (diffSecs < 60) return 'just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        
        return formatDate(dateString);
    } catch (error) {
        return 'N/A';
    }
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

/**
 * Capitalize first letter of each word
 */
export const capitalizeWords = (str) => {
    if (!str) return '';
    return str.replace(/\b\w/g, char => char.toUpperCase());
};

/**
 * Convert string to title case
 */
export const toTitleCase = (str) => {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
};

/**
 * Format phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Format for Indian numbers (10 digits)
    if (cleaned.length === 10) {
        return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
    }
    
    return phoneNumber;
};

/**
 * Get status badge variant
 */
export const getStatusBadgeVariant = (status) => {
    const statusMap = {
        'active': 'success',
        'inactive': 'secondary',
        'pending': 'warning',
        'rejected': 'danger',
        'approved': 'success',
        'cancelled': 'danger',
        'completed': 'success',
        'processing': 'info',
        'lost': 'danger',
        'damaged': 'warning',
        'available': 'success',
        'borrowed': 'info',
        'overdue': 'danger'
    };
    
    return statusMap[status?.toLowerCase()] || 'secondary';
};

/**
 * Get type icon
 */
export const getTypeIcon = (type) => {
    const iconMap = {
        'purchase': '🛒',
        'penalty': '💰',
        'damage': '💔',
        'lost': '🔴',
        'issue': '📚',
        'return': '📤',
        'member': '👤',
        'book': '📖',
        'fine': '⚠️',
        'budget': '📊'
    };
    
    return iconMap[type?.toLowerCase()] || '📄';
};

export default {
    formatCurrency,
    formatDate,
    formatDateTime,
    formatPercentage,
    formatNumber,
    formatFileSize,
    formatDuration,
    formatRelativeTime,
    truncateText,
    capitalizeWords,
    toTitleCase,
    formatPhoneNumber,
    getStatusBadgeVariant,
    getTypeIcon
};
